import {
  ActionIcon,
  Alert,
  Button,
  Drawer,
  Menu,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Textarea,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCalendarClock,
  IconCalendarPause,
  IconClipboardPlus,
  IconClipboardText,
  IconDots,
  IconEye,
  IconHourglass,
  IconInfoCircle,
  IconMessage,
  IconSearch,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { formatDateTime, formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { SortableTh, TablePager } from '../purchasing/table.js';
import { ReturnedBanner } from '../returned/Returned.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import { LoadFailed, PmsBadge, RowsSkeleton, usePmsQuery } from './PmsWorkParts.js';
import styles from './Postponements.module.css';
import { numberFormat, paginate } from './plan-view.js';
import {
  filterPostponements,
  POSTPONEMENT_STATUSES,
  POSTPONEMENT_VIEWS,
  type PostponementSortKey,
  type PostponementViewFilter,
  postponementSummary,
  sortPostponements,
  type WorkOrderShortcut,
  workOrderShortcut,
} from './postponement-view.js';
import type { PmsSource } from './source.js';
import type {
  PostponementActionKind,
  PostponementOfficeEntryView,
  PostponementStatus,
  PostponementView,
  WorkOrderView,
} from './types.js';

export interface PostponementsScreenProps {
  source: PmsSource;
}

type Data =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; items: PostponementView[] };

/** Seçili geminin iş emirleri ve açma yetkisi (iş emri kısayolu; ortak 2026-10-25). */
interface Orders {
  vesselId: string;
  items: WorkOrderView[];
  canCreate: boolean;
}

const PAGE_SIZE = 12;

/**
 * Dar tabloda gizlenme sırası (`fit-columns.ts`): ofis notu sayısı, erteleme süresi, talep eden.
 * Hepsi ayrıntıda görünür; satır menüsü ve durum hiç gizlenmez (ortak 2026-10-25).
 */
const DROP: Record<string, number | undefined> = { office: 1, delay: 2, requestedBy: 3 };
const ALL = 'all';

/** Durumun rozet tonu (`pmsStatusTone`): bekleyen sarı, onaylı yeşil, iade/geri çevrilen kırmızı. */
const STATUS_TONE: Record<PostponementStatus, string> = {
  pending: 'pending',
  approved: 'approved',
  returned: 'returned',
  revoked: 'returned',
};

/**
 * Maintenance (PMS) ▸ Postponements (`/pms/postponements`; ADR 0108 §4): ertelenen işler; iş,
 * ekipman, gerekçe, talep eden ve onaylayan kaptan, zamanlar, orijinal ve yeni vade, erteleme
 * süresi ve durum. Onay gemide biter; ofis gemi ve filo listesinde izler, yorum yazar, ertelemeyi
 * geri çevirir ya da yeni vade verir (gemi senkronda uygular). Ofisin notları ve kararları gemide
 * de görünür. Özet kartları süzgeç kurar; seçimler adreste kalır (`?vessel=&view=&status=&p=`).
 */
export function PostponementsScreen({ source }: PostponementsScreenProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.postponements.${key}`, values);
  const shell = useShell();
  const [query, setQuery] = usePmsQuery();
  const [vessels, setVessels] = useState<PersonnelVessel[] | null>(null);
  const [orders, setOrders] = useState<Orders | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [data, setData] = useState<Data>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: PostponementSortKey; dir: 'asc' | 'desc' }>({
    key: 'requestedAt',
    dir: 'desc',
  });
  const web = source.scope === 'web';
  const requested = query.get('vessel');
  const vesselId =
    web && requested && vessels?.some((v) => v.id === requested)
      ? requested
      : web
        ? null
        : (vessels?.[0]?.id ?? null);
  const view: PostponementViewFilter =
    POSTPONEMENT_VIEWS.find((v) => v === query.get('view')) ?? 'all';
  const status = POSTPONEMENT_STATUSES.find((s) => s === query.get('status')) ?? '';
  const search = query.get('q') ?? '';
  const selectedId = query.get('p');

  useEffect(() => {
    let live = true;
    source
      .vessels()
      .then((list) => {
        if (live) setVessels(list);
      })
      .catch(() => {
        if (live) setVessels([]);
      });
    return () => {
      live = false;
    };
  }, [source]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yeniden okutur
  useEffect(() => {
    if (!source.postponements || vessels === null) return;
    if (!web && vesselId === null) return;
    let live = true;
    source
      .postponements(vesselId)
      .then((items) => {
        if (live) setData({ kind: 'ready', items });
      })
      .catch((error: unknown) => {
        if (live) {
          const code = (error as { code?: unknown } | null)?.code;
          setData({ kind: 'error', code: typeof code === 'string' ? code : 'GENERIC' });
        }
      });
    return () => {
      live = false;
    };
  }, [source, vesselId, vessels, web, reloads]);

  // Seçili geminin iş emirleri (gemide kendi gemisi, ofiste seçilen gemi): satırın iş emri
  // kısayolu Maintenance Plan'dakiyle aynı akış, aynı uç ve aynı yetki (ortak 2026-10-25)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yeniden okutur
  useEffect(() => {
    if (!source.workOrders || vesselId === null) return;
    let live = true;
    source
      .workOrders(vesselId)
      .then((list) => {
        if (!live) return;
        setOrders({
          vesselId,
          items: list.items,
          // Eski sunucu yetkiyi bildirmez: açma ucu yetkiyi yine denetler
          canCreate: Boolean(source.work) && list.can?.create !== false,
        });
      })
      // İş emirleri okunamazsa kısayol çizilmez; liste açılır
      .catch(() => {
        if (live) setOrders(null);
      });
    return () => {
      live = false;
    };
  }, [source, vesselId, reloads]);
  const vesselOrders = orders && orders.vesselId === vesselId ? orders : null;
  const shortcutOf = (item: PostponementView): WorkOrderShortcut | null =>
    vesselOrders && item.vesselId === vesselOrders.vesselId
      ? workOrderShortcut(item, vesselOrders.items, vesselOrders.canCreate)
      : null;
  const goToWorkOrder = (workOrder: Pick<WorkOrderView, 'id' | 'vesselId'>) =>
    shell?.navigate(
      web
        ? `/pms/work-orders?vessel=${workOrder.vesselId}&wo=${workOrder.id}`
        : `/pms/work-orders?wo=${workOrder.id}`,
    );
  const runShortcut = async (item: PostponementView, shortcut: WorkOrderShortcut) => {
    if (shortcut.kind === 'open') {
      goToWorkOrder(shortcut.workOrder);
      return;
    }
    if (!source.work) return;
    setNotice(null);
    try {
      // Maintenance Plan'daki "Create Work Order" ile aynı uç (ikinci yazma yolu yok)
      goToWorkOrder(await source.work.openWorkOrder({ jobId: item.jobId }));
    } catch (error) {
      setNotice(t(`pms.errors.${errorCode(error)}`, { defaultValue: t('pms.errors.GENERIC') }));
    }
  };

  const items = data.kind === 'ready' ? data.items : null;
  const summary = useMemo(() => postponementSummary(items ?? []), [items]);
  const rows = useMemo(
    () => sortPostponements(filterPostponements(items ?? [], { view, status, search }), sort),
    [items, view, status, search, sort],
  );
  const pageView = paginate(rows, page, PAGE_SIZE);
  const selected = items?.find((item) => item.id === selectedId) ?? null;
  const update = (patch: Record<string, string | null>) => {
    setPage(1);
    setQuery(patch);
  };
  const fleet = web && vesselId === null;
  // Dar tabloda önce ofis notu sayısı gizlenir (ayrıntıda görünür); içerik kesilmez
  const tableBox = useFitColumns<HTMLElement>();
  const title = t('menu.pmsPostponements');

  const tile = (
    key: PostponementViewFilter,
    icon: ReactNode,
    value: number,
    tone: 'default' | 'warning' | 'success',
  ) => (
    <button
      key={key}
      type="button"
      className={styles.tileButton}
      aria-pressed={view === key && key !== 'all'}
      onClick={() => update({ view: view === key || key === 'all' ? null : key })}
    >
      <StatTile
        icon={icon}
        label={k(`kpi.${key}`)}
        value={items ? value : '—'}
        tone={tone}
        wrapLabel
      />
    </button>
  );

  const columns: [string, string, PostponementSortKey | null][] = [
    ...(fleet ? ([['vessel', k('columns.vessel'), null]] as [string, string, null][]) : []),
    // Gerekçe işin altında (geniş tablo dar ekranda kaymasın)
    ['job', k('columns.jobReason'), null],
    ['equipment', k('columns.equipment'), null],
    ['requestedBy', k('columns.requestedBy'), 'requestedAt'],
    ['approvedBy', k('columns.approvedBy'), null],
    ['originalDue', k('columns.originalDue'), null],
    ['newDue', k('columns.newDue'), 'newDue'],
    ['delay', k('columns.delay'), 'delay'],
    ['status', k('columns.status'), 'status'],
    ['office', k('columns.office'), null],
  ];

  return (
    <div className={classes.screen} data-pms-screen>
      <PageHeader
        title={title}
        description={k('description')}
        actions={
          web && vessels && vessels.length > 0 ? (
            <span className={work.headerActions}>
              <Select
                aria-label={t('pms.work.vessel')}
                className={classes.vesselPicker}
                size="sm"
                data={[
                  { value: ALL, label: k('allVessels') },
                  ...vessels.map((v) => ({ value: v.id, label: v.name })),
                ]}
                value={vesselId ?? ALL}
                allowDeselect={false}
                onChange={(id) => update({ vessel: id === ALL ? null : id, p: null })}
              />
            </span>
          ) : undefined
        }
      />
      <div className={styles.tiles}>
        {tile('all', <IconCalendarClock size={20} />, summary.all, 'default')}
        {tile(
          'pending',
          <IconHourglass size={20} />,
          summary.pending,
          summary.pending > 0 ? 'warning' : 'default',
        )}
        {tile('active', <IconCalendarPause size={20} />, summary.active, 'success')}
        {tile('office', <IconMessage size={20} />, summary.office, 'default')}
      </div>
      {notice ? (
        <p className={work.error} role="alert">
          {notice}
        </p>
      ) : null}
      {!web && summary.pending > 0 ? (
        <Alert color="yellow" icon={<IconHourglass size={16} />} role="status">
          {k('pendingNotice', { count: summary.pending })}
        </Alert>
      ) : null}
      <Panel title={k('title')}>
        <div className={styles.toolbar}>
          <TextInput
            aria-label={k('search')}
            placeholder={k('search')}
            leftSection={<IconSearch size={15} />}
            value={search}
            onChange={(event) => update({ q: event.currentTarget.value })}
          />
          <Select
            aria-label={k('status')}
            placeholder={k('allStatuses')}
            clearable
            clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
            data={POSTPONEMENT_STATUSES.map((s) => ({ value: s, label: k(`statuses.${s}`) }))}
            value={status || null}
            onChange={(value) => update({ status: value })}
          />
        </div>
        {data.kind === 'error' ? (
          <LoadFailed code={data.code} onRetry={() => setReloads((n) => n + 1)} />
        ) : items === null ? (
          <RowsSkeleton />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<IconCalendarClock size={22} />}
            title={k('empty')}
            description={k('emptyHint')}
          />
        ) : rows.length === 0 ? (
          <EmptyState title={k('noMatch')} />
        ) : (
          <>
            <section
              ref={tableBox}
              className={classes.tableBox}
              aria-label={t('pms.equipment.tableOf', { name: k('title') })}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={classes.table} data-cards>
                <thead>
                  <tr>
                    {columns.map(([key, label, sortKey]) =>
                      sortKey ? (
                        <SortableTh
                          key={key}
                          column={sortKey}
                          label={label}
                          sort={sort}
                          drop={DROP[key]}
                          onSort={(next) => {
                            setSort(next);
                            setPage(1);
                          }}
                        />
                      ) : (
                        <th key={key} scope="col" data-drop={DROP[key]}>
                          {label}
                        </th>
                      ),
                    )}
                    {vesselOrders ? (
                      <th scope="col" className={classes.actionsHead}>
                        <span className={classes.srOnly}>
                          {t('pms.equipment.overview.columns.actions')}
                        </span>
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {pageView.rows.map((item) => (
                    <PostponementRow
                      key={item.id}
                      item={item}
                      fleet={fleet}
                      onOpen={() => update({ p: item.id })}
                      {...(vesselOrders
                        ? {
                            actions: (
                              <RowMenu
                                item={item}
                                shortcut={shortcutOf(item)}
                                onView={() => update({ p: item.id })}
                                onShortcut={(shortcut) => void runShortcut(item, shortcut)}
                              />
                            ),
                          }
                        : {})}
                    />
                  ))}
                </tbody>
              </table>
            </section>
            {rows.length > PAGE_SIZE ? (
              <TablePager
                page={pageView.page}
                pages={pageView.pages}
                summary={t('pms.work.showing', {
                  from: pageView.from,
                  to: pageView.to,
                  total: pageView.total,
                })}
                label={t('pms.work.pages', { name: k('title') })}
                onPage={setPage}
              />
            ) : null}
          </>
        )}
      </Panel>
      <PostponementDrawer
        item={selected}
        source={source}
        shortcut={selected ? shortcutOf(selected) : null}
        onShortcut={(shortcut) => {
          if (selected) void runShortcut(selected, shortcut);
        }}
        onClose={() => setQuery({ p: null })}
        onChanged={() => setReloads((n) => n + 1)}
      />
    </div>
  );
}

/** Vade metni: tarih ve/veya çalışma saati. */
function useDueText() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  return (date: string | null, hours: number | null) =>
    [
      date ? formatDay(date) : null,
      hours === null ? null : t('pms.hours', { value: numberFormat(language, hours) }),
    ]
      .filter(Boolean)
      .join(' · ') || '—';
}

function useDelayText() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  return (item: Pick<PostponementView, 'delayDays' | 'delayHours'>) =>
    [
      item.delayDays === null ? null : t('pms.postponements.delay.days', { count: item.delayDays }),
      item.delayHours === null
        ? null
        : t('pms.postponements.delay.hours', { value: numberFormat(language, item.delayHours) }),
    ]
      .filter(Boolean)
      .join(' · ') || '—';
}

/** Kısayolun metni: açık iş emrine git ya da yeni iş emri aç. */
function useShortcutLabel() {
  const { t } = useTranslation();
  return (shortcut: WorkOrderShortcut) =>
    shortcut.kind === 'open'
      ? t('pms.work.plan.openWorkOrder', { no: shortcut.workOrder.woNo })
      : t('pms.equipment.quickLinks.createWorkOrder');
}

const shortcutIcon = (shortcut: WorkOrderShortcut) =>
  shortcut.kind === 'open' ? <IconClipboardText size={15} /> : <IconClipboardPlus size={15} />;

/** Satır menüsü ("…"): ayrıntı ve iş emri kısayolu (Maintenance Plan'daki menünün dili). */
function RowMenu({
  item,
  shortcut,
  onView,
  onShortcut,
}: {
  item: PostponementView;
  shortcut: WorkOrderShortcut | null;
  onView: () => void;
  onShortcut: (shortcut: WorkOrderShortcut) => void;
}) {
  const { t } = useTranslation();
  const label = useShortcutLabel();
  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          aria-label={t('pms.postponements.more', { code: item.jobCode ?? '' })}
        >
          <IconDots size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<IconEye size={15} />} onClick={onView}>
          {t('pms.postponements.view')}
        </Menu.Item>
        {shortcut ? (
          <Menu.Item leftSection={shortcutIcon(shortcut)} onClick={() => onShortcut(shortcut)}>
            {label(shortcut)}
          </Menu.Item>
        ) : null}
      </Menu.Dropdown>
    </Menu>
  );
}

/** Ertelemenin durum rozetleri: karar; yürürlükte ya da iş yapılınca sona ermiş. */
function StatusBadges({ item }: { item: PostponementView }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.postponements.${key}`);
  return (
    <span className={work.badgeRow}>
      <PmsBadge status={STATUS_TONE[item.status]}>{k(`statuses.${item.status}`)}</PmsBadge>
      {item.active ? <PmsBadge status="postponed">{k('active')}</PmsBadge> : null}
      {!item.active && item.superseded ? (
        <PmsBadge status="cancelled">{k('superseded')}</PmsBadge>
      ) : null}
    </span>
  );
}

export function PostponementRow({
  item,
  fleet,
  onOpen,
  actions,
}: {
  item: PostponementView;
  fleet: boolean;
  onOpen: () => void;
  /** Satır menüsü ("…"); seçili geminin iş emirleri okunduysa. */
  actions?: ReactNode;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.postponements.${key}`, values);
  const due = useDueText();
  const delay = useDelayText();
  const job = [item.jobCode, item.jobTitle].filter(Boolean).join(' · ') || '—';
  return (
    <tr data-revoked={item.status === 'revoked' || undefined}>
      {fleet ? <td data-label={k('columns.vessel')}>{item.vesselName ?? '—'}</td> : null}
      <td data-label={k('columns.jobReason')} className={work.wrapCell}>
        <span className={styles.due}>
          <UnstyledButton className={work.linkButton} onClick={onOpen}>
            {job}
          </UnstyledButton>
          <span className={styles.reason}>{item.reason}</span>
        </span>
      </td>
      <td data-label={k('columns.equipment')} className={work.wrapCell}>
        {item.equipmentName ?? item.equipmentCode ?? '—'}
      </td>
      <td data-label={k('columns.requestedBy')} data-drop={DROP.requestedBy}>
        <span className={styles.due}>
          <span>{item.requestedByName ?? '—'}</span>
          <span className={work.note}>{formatDateTime(item.requestedAt)}</span>
        </span>
      </td>
      <td data-label={k('columns.approvedBy')}>
        {item.approvedByName ? (
          <span className={styles.due}>
            <span>{item.approvedByName}</span>
            <span className={work.note}>{formatDateTime(item.approvedAt)}</span>
          </span>
        ) : (
          '—'
        )}
      </td>
      <td data-label={k('columns.originalDue')}>
        {due(item.originalDueOn, item.originalDueHours)}
      </td>
      <td data-label={k('columns.newDue')}>
        <span className={styles.due}>
          <span>{due(item.newDueOn, item.newDueHours)}</span>
          {item.officeChanged ? <span className={work.note}>{k('officeChanged')}</span> : null}
        </span>
      </td>
      <td data-label={k('columns.delay')} data-drop={DROP.delay}>
        {delay(item)}
      </td>
      <td data-label={k('columns.status')}>
        <StatusBadges item={item} />
      </td>
      <td data-label={k('columns.office')} data-drop={DROP.office}>
        {item.office.length > 0 ? k('officeCount', { count: item.office.length }) : '—'}
      </td>
      {actions !== undefined ? <td className={work.actionsCell}>{actions}</td> : null}
    </tr>
  );
}

/** Ertelemenin ayrıntısı, ofis notları/kararları ve (ofiste, yetkiyle) yeni not ya da karar. */
function PostponementDrawer({
  item,
  source,
  shortcut,
  onShortcut,
  onClose,
  onChanged,
}: {
  item: PostponementView | null;
  source: PmsSource;
  shortcut: WorkOrderShortcut | null;
  onShortcut: (shortcut: WorkOrderShortcut) => void;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const shortcutLabel = useShortcutLabel();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.postponements.${key}`, values);
  const due = useDueText();
  const delay = useDelayText();
  const facts: [string, ReactNode][] = item
    ? [
        [k('detail.vessel'), item.vesselName ?? '—'],
        [k('detail.job'), [item.jobCode, item.jobTitle].filter(Boolean).join(' · ') || '—'],
        [k('detail.equipment'), item.equipmentName ?? item.equipmentCode ?? '—'],
        ...(item.workOrderNo
          ? ([[k('detail.workOrder'), item.workOrderNo]] as [string, ReactNode][])
          : []),
        [k('detail.reason'), item.reason],
        ...(item.classReference
          ? ([[k('detail.classReference'), item.classReference]] as [string, ReactNode][])
          : []),
        [
          k('detail.requested'),
          k('detail.byAt', {
            name: item.requestedByName ?? '—',
            at: formatDateTime(item.requestedAt),
          }),
        ],
        [
          item.status === 'returned' ? k('detail.returned') : k('detail.approved'),
          item.approvedByName
            ? k('detail.byAt', { name: item.approvedByName, at: formatDateTime(item.approvedAt) })
            : (item.returnReason ?? k('detail.waiting')),
        ],
        [k('detail.originalDue'), due(item.originalDueOn, item.originalDueHours)],
        [k('detail.requestedDue'), due(item.requestedDueOn, item.requestedDueHours)],
        [k('detail.newDue'), due(item.newDueOn, item.newDueHours)],
        [k('detail.delay'), delay(item)],
      ]
    : [];
  return (
    <Drawer
      opened={item !== null}
      onClose={onClose}
      position="right"
      size={520}
      title={
        <span className={classes.drawerTitle}>
          {k('detail.title', { code: item?.jobCode ?? '' })}
        </span>
      }
      closeButtonProps={{ 'aria-label': t('pms.drawer.close') }}
    >
      {item ? (
        <div className={work.drawerStack}>
          <span className={work.badgeRow}>
            <StatusBadges item={item} />
            {shortcut ? (
              <Button
                size="xs"
                variant="light"
                leftSection={shortcutIcon(shortcut)}
                onClick={() => onShortcut(shortcut)}
              >
                {shortcutLabel(shortcut)}
              </Button>
            ) : null}
          </span>
          {!item.active && item.superseded ? (
            <p className={work.note}>{k('detail.superseded')}</p>
          ) : null}
          {/* İade ya da ofisin geri çevirmesi (ADR 0116 Ek): kesin; kim, ne zaman, gerekçe */}
          <ReturnedBanner
            notice={item.returnNotice}
            hint
            {...(item.status === 'revoked' ? { kind: 'revoked' as const } : {})}
          />
          <Alert color="blue" icon={<IconInfoCircle size={16} />}>
            {k('detail.shipOnly')}
          </Alert>
          <dl className={work.facts}>
            {facts.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          {item.classItem ? <p className={work.note}>{k('detail.classItem')}</p> : null}
          <h3 className={styles.sectionTitle}>{k('thread.title')}</h3>
          {item.office.length === 0 ? (
            <p className={work.note}>{k('thread.empty')}</p>
          ) : (
            <ul className={styles.thread}>
              {item.office.map((entry) => (
                <OfficeEntry key={entry.id} entry={entry} />
              ))}
            </ul>
          )}
          {source.office?.postponementAction && item.canAct ? (
            <OfficeActionForm key={item.id} item={item} source={source} onSent={onChanged} />
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
}

export function OfficeEntry({ entry }: { entry: PostponementOfficeEntryView }) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.postponements.${key}`, values);
  const due = useDueText();
  const decision = entry.kind !== 'comment';
  const result = entry.result;
  return (
    <li className={styles.entry}>
      <span className={styles.entryHead}>
        <span>{k(`thread.kinds.${entry.kind}`)}</span>
        {decision ? (
          <PmsBadge
            status={
              result === null ? 'pending' : result.outcome === 'applied' ? 'approved' : 'returned'
            }
          >
            {k(result === null ? 'thread.sent' : `thread.${result.outcome}`)}
          </PmsBadge>
        ) : null}
      </span>
      <span className={styles.entryMeta}>
        {k('detail.byAt', { name: entry.byName ?? '—', at: formatDateTime(entry.at) })}
      </span>
      {entry.kind === 'change_due' ? (
        <span className={styles.entryMeta}>
          {k('thread.newDue', { due: due(entry.newDueOn, entry.newDueHours) })}
          {entry.seenDueOn || entry.seenDueHours !== null
            ? ` · ${k('thread.seen', { due: due(entry.seenDueOn, entry.seenDueHours) })}`
            : ''}
        </span>
      ) : null}
      <p className={styles.entryText}>{entry.text}</p>
      {result?.outcome === 'rejected' ? (
        <span className={styles.entryMeta}>
          {k('thread.rejectedReason', { reason: errorText({ code: result.code ?? 'GENERIC' }) })}
        </span>
      ) : null}
    </li>
  );
}

export function OfficeActionForm({
  item,
  source,
  onSent,
}: {
  item: PostponementView;
  source: PmsSource;
  onSent: () => void;
}) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.postponements.${key}`, values);
  const waiting = item.office.some((e) => e.kind !== 'comment' && e.result === null);
  const decidable = item.status === 'approved' && item.active && !waiting;
  const [kind, setKind] = useState<PostponementActionKind>('comment');
  const [text, setText] = useState('');
  const [newDueOn, setNewDueOn] = useState<string>('');
  const [newDueHours, setNewDueHours] = useState<number | ''>('');
  const [classReference, setClassReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState<string | null>(null);
  const reauth = useReauth('postponement_request');
  const decision = kind !== 'comment';
  const submit = async () => {
    if (!source.office?.postponementAction) return;
    if (decision && reauth.missing) return;
    setBusy(true);
    setError(null);
    setSent(null);
    try {
      await source.office.postponementAction(item.id, {
        kind,
        text,
        newDueOn: kind === 'change_due' && newDueOn !== '' ? newDueOn : null,
        newDueHours: kind === 'change_due' && newDueHours !== '' ? newDueHours : null,
        classReference: kind === 'change_due' && classReference.trim() ? classReference : null,
        ...(decision ? reauth.body() : {}),
      });
      setText('');
      setNewDueOn('');
      setNewDueHours('');
      setClassReference('');
      setSent(decision ? k('action.sentDecision') : k('action.sentComment'));
      setKind('comment');
      onSent();
    } catch (failure) {
      reauth.noteError(failure);
      setError(failure);
    } finally {
      setBusy(false);
    }
  };
  const kinds: PostponementActionKind[] = ['comment', 'change_due', 'revoke'];
  return (
    <Stack gap="sm">
      <h3 className={styles.sectionTitle}>{k('action.title')}</h3>
      <SegmentedControl
        aria-label={k('action.kind')}
        value={kind}
        onChange={(value) => setKind(value as PostponementActionKind)}
        data={kinds.map((value) => ({
          value,
          label: k(`thread.kinds.${value}`),
          disabled: value !== 'comment' && !decidable,
        }))}
        fullWidth
      />
      {!decidable ? (
        <p className={work.note}>
          {waiting ? k('action.pending') : item.status === 'pending' ? k('action.notApproved') : ''}
        </p>
      ) : null}
      <p className={work.note}>
        {k(
          kind === 'comment'
            ? 'action.commentHint'
            : kind === 'revoke'
              ? 'action.revokeHint'
              : 'action.changeHint',
        )}
      </p>
      {kind === 'change_due' ? (
        <>
          {item.originalDueOn !== null ? (
            <DayInput
              label={k('action.newDueOn')}
              value={newDueOn || null}
              onChange={(value) => setNewDueOn(value ?? '')}
            />
          ) : null}
          {item.originalDueHours !== null ? (
            <NumberInput
              label={k('action.newDueHours')}
              min={0}
              decimalScale={1}
              value={newDueHours}
              onChange={(value) => setNewDueHours(typeof value === 'number' ? value : '')}
            />
          ) : null}
          {item.classItem ? (
            <TextInput
              label={k('action.classReference')}
              description={k('action.classHint')}
              value={classReference}
              onChange={(event) => setClassReference(event.currentTarget.value)}
            />
          ) : null}
        </>
      ) : null}
      <Textarea
        label={k(kind === 'comment' ? 'action.text' : 'action.reason')}
        autosize
        minRows={2}
        maxLength={2000}
        value={text}
        onChange={(event) => setText(event.currentTarget.value)}
      />
      {decision ? (
        <ReauthField
          reauth={reauth}
          label={t('pms.work.password.password')}
          description={t('pms.work.password.hint')}
        />
      ) : null}
      {error ? (
        <p className={work.error} role="alert">
          {errorText(error)}
        </p>
      ) : null}
      {sent ? (
        <p className={work.note} role="status">
          {sent}
        </p>
      ) : null}
      <div className={work.dialogActions}>
        <Button
          onClick={submit}
          loading={busy}
          {...(kind === 'revoke' ? { color: 'red' } : {})}
          disabled={text.trim() === '' || (decision && (reauth.missing || !decidable))}
        >
          {k(decision ? 'action.submitDecision' : 'action.submitComment')}
        </Button>
      </div>
    </Stack>
  );
}
